import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Phone,
  EnvelopeSimple,
  MapPin,
  Storefront,
  Camera,
  Plus,
  CalendarPlus,
  CurrencyDollar,
  PhoneCall,
  PhoneOutgoing,
  PhoneIncoming,
} from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [visits, setVisits] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("visits");

  useEffect(() => {
    fetchCustomer();
    fetchVisits();
    fetchCalls();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      setCustomer(response.data);
    } catch (error) {
      toast.error("Customer not found");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    try {
      const response = await axios.get(`${API}/visits`, {
        params: { customer_id: id },
      });
      setVisits(response.data);
    } catch (error) {
      console.error("Failed to fetch visits");
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/calls`, {
        params: { customer_id: id },
      });
      setCalls(response.data);
    } catch (error) {
      console.error("Failed to fetch calls");
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (error) {
      toast.error("Failed to delete customer");
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy 'at' h:mm a") + " IST";
    } catch {
      return dateStr;
    }
  };

  const getPhotoUrl = (path) => {
    const token = localStorage.getItem("token");
    return `${API}/files/${path}?auth=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="customer-detail-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="customer-detail-page">
      {/* Header */}
      <div className="top-bar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/customers")}
            className="p-2 -ml-2 text-[#4B5563] hover:text-[#0A0A0A]"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <p className="overline">CUSTOMER</p>
            <h1 className="top-bar-title truncate">{customer.name}</h1>
          </div>
          <button
            onClick={() => navigate(`/customers/${id}/edit`)}
            className="p-2 text-[#4B5563] hover:text-[#0055FF]"
            data-testid="edit-customer-button"
          >
            <PencilSimple size={24} />
          </button>
          <button
            onClick={() => setDeleteDialog(true)}
            className="p-2 text-[#4B5563] hover:text-[#EF4444]"
            data-testid="delete-customer-button"
          >
            <Trash size={24} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Customer Info Card */}
        <div className="card-flat p-4 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
              <Storefront size={28} className="text-[#0055FF]" weight="fill" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-[#0A0A0A]">
                {customer.company_name || customer.name}
              </h2>
              {customer.company_name && (
                <p className="text-[#4B5563]">Contact: {customer.name}</p>
              )}
            </div>
          </div>

          {customer.business_type && (
            <span className="inline-block mb-4 text-sm px-3 py-1 bg-[#F3F4F6] rounded-full text-[#4B5563]">
              {customer.business_type}
            </span>
          )}

          <div className="space-y-3">
            {customer.phone && (
              <a
                href={`tel:${customer.phone}`}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F3F4F6] transition-colors"
                data-testid="customer-phone"
              >
                <Phone size={20} className="text-[#10B981]" weight="fill" />
                <span className="text-[#0A0A0A]">{customer.phone}</span>
              </a>
            )}

            {customer.email && (
              <a
                href={`mailto:${customer.email}`}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F3F4F6] transition-colors"
                data-testid="customer-email"
              >
                <EnvelopeSimple size={20} className="text-[#0055FF]" weight="fill" />
                <span className="text-[#0A0A0A]">{customer.email}</span>
              </a>
            )}

            {customer.address && (
              <div
                className="flex items-start gap-3 p-3 rounded-lg"
                data-testid="customer-address"
              >
                <MapPin size={20} className="text-[#F59E0B] mt-0.5" weight="fill" />
                <span className="text-[#0A0A0A]">{customer.address}</span>
              </div>
            )}
          </div>

          {customer.notes && (
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <p className="overline mb-2">NOTES</p>
              <p className="text-[#4B5563]" data-testid="customer-notes">{customer.notes}</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2 animate-fade-in-up stagger-1">
          <Button
            onClick={() => navigate(`/customers/${id}/visit`)}
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1 border-[#E5E7EB] hover:border-[#0055FF] hover:bg-[#F0F5FF]"
            data-testid="add-visit-button"
          >
            <Camera size={22} className="text-[#0055FF]" />
            <span className="text-xs font-medium">Visit</span>
          </Button>
          <Button
            onClick={() => navigate(`/customers/${id}/call`)}
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1 border-[#E5E7EB] hover:border-[#10B981] hover:bg-[#D1FAE5]"
            data-testid="add-call-button"
          >
            <PhoneCall size={22} className="text-[#10B981]" />
            <span className="text-xs font-medium">Call</span>
          </Button>
          <Button
            onClick={() => navigate(`/schedule?customer=${id}`)}
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1 border-[#E5E7EB] hover:border-[#8B5CF6] hover:bg-[#EDE9FE]"
            data-testid="schedule-visit-button"
          >
            <CalendarPlus size={22} className="text-[#8B5CF6]" />
            <span className="text-xs font-medium">Schedule</span>
          </Button>
          <Button
            onClick={() => navigate(`/sales?customer=${id}`)}
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1 border-[#E5E7EB] hover:border-[#F59E0B] hover:bg-[#FEF3C7]"
            data-testid="add-sale-button"
          >
            <CurrencyDollar size={22} className="text-[#F59E0B]" />
            <span className="text-xs font-medium">Sale</span>
          </Button>
        </div>

        {/* Activity Tabs */}
        <div className="flex gap-2 animate-fade-in-up stagger-2">
          <button
            onClick={() => setActiveTab("visits")}
            className={`flex-1 py-2 px-3 rounded-full font-medium text-sm transition-colors ${
              activeTab === "visits"
                ? "bg-[#0055FF] text-white"
                : "bg-white border border-[#E5E7EB] text-[#4B5563]"
            }`}
          >
            Visits ({visits.length})
          </button>
          <button
            onClick={() => setActiveTab("calls")}
            className={`flex-1 py-2 px-3 rounded-full font-medium text-sm transition-colors ${
              activeTab === "calls"
                ? "bg-[#10B981] text-white"
                : "bg-white border border-[#E5E7EB] text-[#4B5563]"
            }`}
          >
            Calls ({calls.length})
          </button>
        </div>

        {/* Visit History */}
        {activeTab === "visits" && (
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Visit History
            </h3>
          </div>

          {visits.length > 0 ? (
            <div className="card-flat overflow-hidden">
              {visits.map((visit, index) => (
                <div
                  key={visit.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`visit-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {visit.photo_path ? (
                      <img
                        src={getPhotoUrl(visit.photo_path)}
                        alt="Visit"
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        data-testid={`visit-photo-${index}`}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                        <Camera size={24} className="text-[#9CA3AF]" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0A0A0A]">
                        {formatDate(visit.visit_date)}
                      </p>
                      {visit.notes && (
                        <p className="text-sm text-[#4B5563] mt-1">{visit.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-flat p-6 text-center">
              <Camera size={32} className="mx-auto text-[#D1D5DB] mb-2" />
              <p className="text-[#9CA3AF]">No visits recorded yet</p>
              <Button
                onClick={() => navigate(`/customers/${id}/visit`)}
                className="mt-3 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
                data-testid="first-visit-button"
              >
                <Plus size={20} weight="bold" className="mr-1" />
                Add First Visit
              </Button>
            </div>
          )}
        </div>
        )}

        {/* Call History */}
        {activeTab === "calls" && (
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Call History
            </h3>
          </div>

          {calls.length > 0 ? (
            <div className="card-flat overflow-hidden">
              {calls.map((call, index) => (
                <div
                  key={call.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`call-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      call.call_type === "inbound" ? "bg-[#D1FAE5]" : "bg-[#DBEAFE]"
                    }`}>
                      {call.call_type === "inbound" ? (
                        <PhoneIncoming size={20} className="text-[#10B981]" weight="fill" />
                      ) : (
                        <PhoneOutgoing size={20} className="text-[#0055FF]" weight="fill" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[#0A0A0A]">
                          {call.call_type === "inbound" ? "Inbound Call" : "Outbound Call"}
                        </p>
                        {call.duration_minutes && (
                          <span className="text-xs px-2 py-0.5 bg-[#F3F4F6] rounded text-[#4B5563]">
                            {call.duration_minutes} min
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#9CA3AF]">{formatDate(call.call_date)}</p>
                      {call.notes && (
                        <p className="text-sm text-[#4B5563] mt-2">{call.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-flat p-6 text-center">
              <PhoneCall size={32} className="mx-auto text-[#D1D5DB] mb-2" />
              <p className="text-[#9CA3AF]">No calls logged yet</p>
              <Button
                onClick={() => navigate(`/customers/${id}/call`)}
                className="mt-3 bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-full"
                data-testid="first-call-button"
              >
                <Plus size={20} weight="bold" className="mr-1" />
                Log First Call
              </Button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {customer.name}? This will also delete all visits, sales, and schedules for this customer. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(false)}
              data-testid="cancel-delete-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-button"
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
